import { IssueFragment, IssueFragmentQuery } from '../../utils/gqlModels';
import {
  Issue,
  NON_VALIDATE_PAGE_SIZE,
  Project,
  VALIDATE_PAGE_SIZE,
} from '../../utils/model';
import { graphqlQuery } from './_graphqlQuery';
import {
  AddIncorrectTimeNoteFunction,
  checkIssueIncorrectTimeNotes,
  issueMapFunction,
} from '../../utils/issueUtils';

const getProjectIssuesQuery = (validateNotes: boolean) => `
${IssueFragmentQuery(validateNotes)}
query issues($pid: ID!, $first: Int, $after: String, $updatedAfter: Time, $createdBefore:Time) {
  project(fullPath: $pid) {
    issues(
      first: $first
      sort: UPDATED_DESC
      after: $after
      state: all
      updatedAfter: $updatedAfter
      createdBefore: $createdBefore
    ) {
      pageInfo {
        endCursor
      }
      count
      nodes {
        ...IssueFragment
      }
    }
  }
}
`;

interface GetProjectIssueGqlResponse {
  project: {
    issues: {
      pageInfo: {
        endCursor: string;
      };
      count: number;
      nodes: IssueFragment[];
    };
  };
}

const getProjectIssuesGqlPaged = async ({
  project,
  projects,
  from,
  to,
  endCursor,
  validateNotes,
  addIncorrectTimeNote,
}: {
  project: Project;
  projects: Project[];
  from: Date;
  to: Date;
  endCursor: string | undefined;
  addIncorrectTimeNote: AddIncorrectTimeNoteFunction;
  validateNotes: boolean;
}): Promise<{ issues: Issue[]; endCursor: string; total: number }> => {
  const data = await graphqlQuery<GetProjectIssueGqlResponse>(
    getProjectIssuesQuery(validateNotes),
    {
      pid: project.fullPath,
      first: validateNotes ? VALIDATE_PAGE_SIZE : NON_VALIDATE_PAGE_SIZE,
      after: endCursor,
      updatedAfter: from.toISOString(),
      createdBefore: to.toISOString(),
    },
  );

  const promises = data.project.issues.nodes.map(issueMapFunction({ project, projects }));
  checkIssueIncorrectTimeNotes({
    addIncorrectTimeNote,
    issues: data.project.issues.nodes,
    from,
    to,
    project,
    projects,
  });
  const issues = await Promise.all(promises);
  return {
    issues,
    endCursor: data.project.issues.pageInfo.endCursor,
    total: data.project.issues.count,
  };
};

export const getProjectIssuesGql = async ({
  project,
  projects,
  from,
  to,
  setProgress,
  addIncorrectTimeNote,
  validateNotes,
}: {
  project: Project;
  projects: Project[];
  from: Date;
  to: Date;
  setProgress: (progress: string) => void;
  addIncorrectTimeNote: AddIncorrectTimeNoteFunction;
  validateNotes: boolean;
}): Promise<Issue[]> => {
  let endCursor: string | undefined;
  const issues: Issue[] = [];
  setProgress('0 / ?');
  let curLoaded = 0;
  do {
    const {
      issues: newIssues,
      endCursor: newEndCursor,
      total,
    } = await getProjectIssuesGqlPaged({
      project,
      from,
      to,
      validateNotes,
      endCursor,
      projects,
      addIncorrectTimeNote,
    });
    curLoaded += newIssues.length;
    setProgress(`${curLoaded} / ${total}`);
    issues.push(...newIssues);
    endCursor = newEndCursor;
  } while (endCursor);
  return issues;
};
